import { OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.types";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { toggleVisible } from "@cervval.js/client-html5-core/src/tools/css.tools";
import { FLEX_CONTAINER_COLUMN, FLEX_ITEM, H_100, PADDING, W_100 } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { empty } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { CustomElement, NAVIGATION_SERVICE, NavigationService, WebPageComponent } from "@cervval.js/client-html5-spa/navigation";
import { CjsButton } from "@cervval.js/client-html5-ui/src/components/button/button";
import { CjsIconFa } from "@cervval.js/client-html5-ui/src/components/icon/iconFa";
import { Injected } from "@cervval.js/common/src/dependencies/injected";
import { translateHTMLReferences } from "@ehealth_tools/client-html-tools/src/tools/webcomponent.tools";
import { MapComponent } from "@ehealth_tools/client-map/src/components/map/mapComponent";
import { BookingProfessional } from "@ehealth_tools/common-booking/src/data/booking.dto";
import { BOOKING_SERVICE } from "@ehealth_tools/common-booking/src/services/abstractCommonBooking.service";
import { MapProfessional } from "@ehealth_tools/common-map/src/map.definitions";
import { BookingUserCard } from "../../../../components/bookingUserCard/bookingUserCard";
import { AbstractClientBookingService } from "../../../../services/abstractClientBooking.service";
import { BookingPageName } from "../../../booking.pages";
import { I18nProfessional } from "./professionalPage.i18n";

export interface ProfessionalPageOptions {
    /** Google Maps API Token to use to load map */
    googleApiToken?: string;
    googleMapUid?: string;
}

const defaultProfessionalPageOptions: ProfessionalPageOptions = {};

@CustomElement({
    name: "professional-page",
    style: require("./professionalPage.scss"),
    template: require("./professionalPage.html"),
    i18n: require("./professionalPage.i18n").I18n(),
    elementCssClasses: [W_100, H_100, FLEX_CONTAINER_COLUMN, FLEX_ITEM, PADDING.PY_2]
})
export class Professional<P extends MapProfessional, I18n extends I18nProfessional = I18nProfessional> extends WebPageComponent<I18n> {
    @TypedHtmlElements()
    public readonly components!: {
        container: HTMLDivElement,
        returnDiv: HTMLDivElement,
        returnIcon: CjsIconFa,
        returnTitle: HTMLDivElement,
        listTitle: HTMLDivElement,
        professionalList: HTMLDivElement,
        addPro: CjsButton,
        mapDisplay: HTMLDivElement,
        professionalInfo: HTMLDivElement,
        appointmentsTitle: HTMLDivElement,
        appointmentsList: HTMLDivElement
    };

    @TypedWebComponentOptions(defaultProfessionalPageOptions)
    public readonly options!: WebComponentOptions<ProfessionalPageOptions>;

    constructor(options?: Partial<ProfessionalPageOptions>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);
        this.options.setWithoutRefresh(options);
    }

    @Injected(NAVIGATION_SERVICE)
    private readonly navigationService!: NavigationService<BookingPageName>;

    @Injected(BOOKING_SERVICE)
    private _bookingService!: AbstractClientBookingService;

    private _professionalsList: BookingProfessional[] = [];

    private _mapComponent!: MapComponent<P>;

    private _currentPro: BookingProfessional | undefined;

    /** @inheritdoc */
    public override init(): void {
        this.components.returnDiv.addEventListener("click", () => {
            this.navigationService.navigate("profile");
        });
        this._initIcons();

        this.components.addPro.options.setAndRefreshIfChanged({
            iconMode: "before",
            cssClasses: ["addPro"],
            icon: {
                name: "fa-user-plus"
            },
            label: this.i18n.addButton
        });

        // Set map component
        this._mapComponent = new MapComponent({
            apiToken: this.options.googleApiToken,
            mapID: this.options.googleMapUid
        });
        this.components.mapDisplay.append(this._mapComponent);
    }

    /**
     * Init icons
     */
    private _initIcons(): void {
        this.components.returnIcon.options.setAndRefreshIfChanged({
            name: "fa-chevron-left"
        });
    }

    /** @inheritdoc */
    public override refresh(): void {
        this._currentPro = undefined;
        this._refreshI18n();
        this._professionalsList = this._bookingService.getProfessionalsOfCurrentUser();
        this._refreshAppointmentCards();
        this._displayProInfo();
    }

    /**
     * Refresh i18n elements
     */
    private _refreshI18n(): void {
        translateHTMLReferences(this.components, this.i18n.html);
    }

    /**
     * Add cards to the list of professional
     */
    private _refreshAppointmentCards(): void {
        empty(this.components.professionalList);
        for (const professional of this._professionalsList) {
            this.components.professionalList.append(new BookingUserCard({
                user: professional,
                infoCallback: () => {
                    this._currentPro = professional;
                    return this._displayProInfo();
                }
            }));
        }
    }

    /**
     * Display infos of a professional
     */
    private _displayProInfo(): void {
        let isVisible = false;
        if (this._currentPro != null) {
            isVisible = true;
        }
        toggleVisible(this.components.professionalInfo, isVisible);
    }

}
